"use client";

import { useEffect } from "react";

import { markUserAsLead, trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// Calendly booking conversion tracker.
//
// The /schedule page embeds Calendly in a cross-origin <iframe>, so a completed
// booking is invisible to us by default — which is why discovery calls have
// recorded ZERO conversions despite real bookings.
//
// Calendly broadcasts its lifecycle to the parent window via postMessage:
//   calendly.profile_page_viewed
//   calendly.event_type_viewed
//   calendly.date_and_time_selected
//   calendly.event_scheduled   <- the booking is CONFIRMED
//
// We listen for `calendly.event_scheduled` and fire book_discovery_call, the
// highest-intent conversion on the site.
//
// Security: we verify the message actually originates from a calendly.com
// origin before trusting it, so another embedded frame can't spoof a
// conversion. We deliberately do NOT read the payload's invitee details —
// Calendly includes URIs to the event/invitee and we keep PII out of analytics
// entirely.
// ----------------------------------------------------------------------------

const isCalendlyOrigin = (origin: string): boolean => {
  try {
    const { hostname, protocol } = new URL(origin);
    return (
      protocol === "https:" &&
      (hostname === "calendly.com" || hostname.endsWith(".calendly.com"))
    );
  } catch {
    return false;
  }
};

type CalendlyMessage = { event?: unknown };

const CalendlyConversionTracker = () => {
  useEffect(() => {
    // Guard against double-firing if Calendly emits the event more than once
    // (e.g. the iframe re-renders after confirmation).
    let fired = false;

    const onMessage = (e: MessageEvent) => {
      if (!isCalendlyOrigin(e.origin)) return;

      const data = e.data as CalendlyMessage | null;
      const name = typeof data?.event === "string" ? data.event : "";
      if (name !== "calendly.event_scheduled") return;
      if (fired) return;
      fired = true;

      const commonParams = {
        lead_source: "discovery_call",
        form_id: "discovery_call",
        form_name: "calendly_discovery_call",
        booking_source: "schedule_page",
      } as const;

      // THE conversion event — GA4 key event, imported by Google Ads as the
      // single primary conversion. The `fired` guard above means this runs
      // exactly once even if Calendly re-emits event_scheduled.
      // No PII — Calendly's payload carries invitee URIs which we ignore.
      trackEvent("generate_lead", commonParams);
      // GA4-only segmentation signal. Do NOT build a Google Ads tag on this —
      // it fires alongside generate_lead and would double-count.
      trackEvent("book_discovery_call", commonParams);
      markUserAsLead();
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return null;
};

export default CalendlyConversionTracker;
